// src/pages/training-videos-library/components/VideoPlayer.jsx
// Replaces the <video> tag with a YouTube iframe embed.
// Tracks progress via YouTube IFrame API postMessage.

import React, { useState, useRef, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { getYouTubeEmbedUrl } from '../../../lib/supabase';

const VideoPlayer = ({ video, onClose, onComplete, onProgress }) => {
  const iframeRef           = useRef(null);
  const [isReady, setIsReady]           = useState(false);
  const [currentTime, setCurrentTime]   = useState(0);
  const [duration, setDuration]         = useState(video?.duration || 0);
  const [isPlaying, setIsPlaying]       = useState(false);
  const [volume, setVolume]             = useState(100);        // 0-100
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const playerRef           = useRef(null);   // YT.Player instance
  const pollRef             = useRef(null);
  const progressSavedRef    = useRef(false);

  // ── Build embed URL ─────────────────────────────────────────
  const ytId      = video?.youtubeId;
  const embedUrl  = getYouTubeEmbedUrl(ytId, { autoplay: true });

  // ── Format helper ────────────────────────────────────────────
  const formatTime = (s = 0) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  // ── Load YouTube IFrame API ──────────────────────────────────
  useEffect(() => {
    function initPlayer() {
      if (!window.YT || !iframeRef.current) return;
      playerRef.current = new window.YT.Player(iframeRef.current, {
        events: {
          onReady: (e) => {
            setIsReady(true);
            const dur = e.target.getDuration();
            if (dur) setDuration(dur);
            e.target.setVolume(volume);
          },
          onStateChange: (e) => {
            const YT = window.YT.PlayerState;
            setIsPlaying(e.data === YT.PLAYING);
            if (e.data === YT.ENDED) {
              onComplete?.(video?.id);
              clearInterval(pollRef.current);
            }
          },
        },
      });
    }

    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(tag);
      window.onYouTubeIframeAPIReady = initPlayer;
    } else {
      initPlayer();
    }

    return () => {
      clearInterval(pollRef.current);
      try { playerRef.current?.destroy(); } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ytId]);

  // ── Poll currentTime every second while playing ───────────────
  useEffect(() => {
    clearInterval(pollRef.current);
    if (isPlaying && isReady) {
      pollRef.current = setInterval(() => {
        const ct = playerRef.current?.getCurrentTime?.() || 0;
        const dur = playerRef.current?.getDuration?.() || duration;
        setCurrentTime(ct);
        if (dur) setDuration(dur);

        const pct = Math.round((ct / dur) * 100);
        onProgress?.(video?.id, pct, pct >= 95);

        // Mark completed at 95%
        if (pct >= 95 && !progressSavedRef.current) {
          progressSavedRef.current = true;
          onComplete?.(video?.id);
        }
      }, 1000);
    }
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, isReady]);

  // ── Controls ─────────────────────────────────────────────────
  const togglePlay = () => {
    if (!playerRef.current) return;
    isPlaying ? playerRef.current.pauseVideo() : playerRef.current.playVideo();
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pos  = (e.clientX - rect.left) / rect.width;
    const t    = pos * duration;
    playerRef.current?.seekTo(t, true);
    setCurrentTime(t);
  };

  const handleVolumeChange = (e) => {
    const v = parseInt(e.target.value, 10);
    setVolume(v);
    playerRef.current?.setVolume(v);
    if (v === 0) playerRef.current?.mute();
    else playerRef.current?.unMute();
  };

  const handleSpeedChange = (e) => {
    const s = parseFloat(e.target.value);
    setPlaybackSpeed(s);
    playerRef.current?.setPlaybackRate(s);
  };

  const progress = duration ? (currentTime / duration) * 100 : 0;

  // ── Keyboard shortcuts ────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === ' ') { e.preventDefault(); togglePlay(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, isReady]);

  return (
    <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center">
      <div className="w-full h-full max-w-7xl mx-auto p-4 flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <div className="flex-1 min-w-0 pr-4">
            <h2 className="text-xl font-semibold text-white truncate">{video?.title}</h2>
            <p className="text-sm text-white/60 mt-0.5 line-clamp-1">{video?.description}</p>
          </div>
          <Button variant="ghost" onClick={onClose} iconName="X" iconSize={24} className="text-white shrink-0">
            Fermer
          </Button>
        </div>

        {/* Player area */}
        <div className="flex-1 min-h-0 relative bg-black rounded-xl overflow-hidden">
          {/* YouTube iframe */}
          <iframe
            ref={iframeRef}
            src={embedUrl}
            title={video?.title}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />

          {/* Custom bottom controls overlay */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent px-4 pb-4 pt-10 pointer-events-none">

            {/* Progress bar */}
            <div
              className="w-full h-1.5 bg-white/20 rounded-full cursor-pointer mb-3 pointer-events-auto"
              onClick={handleSeek}
              title="Seek"
            >
              <div
                className="h-full bg-primary rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>

            <div className="flex items-center justify-between pointer-events-auto">
              {/* Left controls */}
              <div className="flex items-center gap-3">
                <button
                  onClick={togglePlay}
                  disabled={!isReady}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors disabled:opacity-40"
                >
                  <Icon name={isPlaying ? 'Pause' : 'Play'} size={22} color="#ffffff" />
                </button>

                {/* Volume */}
                <div className="flex items-center gap-1.5">
                  <Icon name={volume === 0 ? 'VolumeX' : volume < 50 ? 'Volume1' : 'Volume2'} size={18} color="#ffffff" />
                  <input
                    type="range"
                    min={0} max={100} step={5}
                    value={volume}
                    onChange={handleVolumeChange}
                    className="w-20 accent-primary"
                  />
                </div>

                {/* Time */}
                <span className="text-sm text-white/80 tabular-nums">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
              </div>

              {/* Right controls */}
              <div className="flex items-center gap-3">
                {/* Speed */}
                <select
                  value={playbackSpeed}
                  onChange={handleSpeedChange}
                  className="px-2 py-1 bg-white/10 text-white text-sm rounded border border-white/20 cursor-pointer"
                >
                  {[0.5, 0.75, 1, 1.25, 1.5, 2].map((s) => (
                    <option key={s} value={s}>{s}x</option>
                  ))}
                </select>

                {/* YouTube link */}
                <a
                  href={video?.youtubeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  title="Ouvrir sur YouTube"
                >
                  <Icon name="ExternalLink" size={18} color="#ffffff" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;